import React, { BaseSyntheticEvent } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { useIntl, FormattedMessage } from "react-intl";
import moment from "moment";

import {
  NumberInput,
  Checkbox,
  RadioButtonGroup,
  Select,
  SelectItem,
  Input,
  Blockquote,
} from "@wfp/ui";

import ModalExtended from "components/ModalExtended";
import DateInput from "components/DateInput";
import { Repeater, RepeaterItem, RepeaterTitle } from "components/Repeater";
import AddButton from "components/RepeaterAddButton";
import ErrorList from "components/ErrorList";

import {
  studentKinds,
  allLevels,
  studentLevelValues,
  selectContainerStyles,
  deliveryCategory,
} from "SCConstants";
import useStore from "helpers/useStore";

import { schoolYearValidationResolver } from "./schoolYearValidation";
import styles from "./styles.module.scss";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getAllStores,
  getIsSyncableLocked,
  getPreviousSchoolYears,
  getCurrentSchoolYear,
  getSchoolYearByDate,
  SchoolYear,
  SchoolYearLevel,
  isDeliveryStore,
  isPurchaseDetailStore,
} from "data-handler/ducks/stores";
import { getModelDateFormat } from "components/UneditableMessage";

import Gate from "../Gate";
import { getLevelAgeMappings } from "data-handler/ducks/ageGroups";

import {
  getInitialCarryOverStock,
  getInitialCarryOverPurchaseStock,
} from "helpers/stock";
import { incidentTypes } from "components/IncidentEdit/index";
import {
  getIsReportLocked,
  getReportByMoment,
} from "data-handler/ducks/reportsV3";

import "./styles.scss";

import { RootState } from "data-handler/rootReducer";

type FormValues = SchoolYear & Record<"causes", boolean[]>;

const storeToFormValues = (store: SchoolYear) => {
  const _store: FormValues = JSON.parse(JSON.stringify(store)); // deep copy

  const causes: boolean[] = [];

  if ("causes" in _store) {
    // TODO find out if this is a valid code. School year does not have causes
    // _store is incident?
    ((_store.causes as unknown) as number[]).forEach(
      (causeFk: number) => (causes[causeFk] = true)
    );
  }

  // Cast initial enrolment figures to strings
  if ("levels" in _store && _store.levels) {
    _store.levels.forEach((level: SchoolYearLevel) => {
      studentKinds.forEach((kind) => {
        level[`initial_${kind.value}`] = `${
          level[`initial_${kind.value}`]
        }` as any;
      });
    });
  }

  const reasonValue = (_store as any)?.reason;
  const reason = { label: incidentTypes[reasonValue], value: reasonValue };

  return { ..._store, reason, causes };
};

const formValuesToStore = (values: any) => {
  // Cast initial enrolment figures to ints
  values.levels.forEach((level: any) => {
    studentKinds.forEach((kind) => {
      level[`initial_${kind.value}`] =
        parseInt(level[`initial_${kind.value}`]) || 0;
    });
  });

  // Sort levels in-place according to business rules (tl;dr: increasing student age)
  values.levels.sort(
    (a: SchoolYearLevel, b: SchoolYearLevel) =>
      studentLevelValues.indexOf(a.level) - studentLevelValues.indexOf(b.level)
  );

  return values;
};

type YearEditProps = {
  currentStoreData: SchoolYear;
};
const YearEdit: React.ElementType<YearEditProps> = ({ currentStoreData }) => {
  const intl = useIntl();
  const params: Record<string, string> = useParams();
  const previousSchoolYears = useSelector(getPreviousSchoolYears);
  const currentYear = useSelector((state: RootState) =>
    getCurrentSchoolYear(state)
  );

  const firstSchoolYear = previousSchoolYears.length === 0;

  const carryOverDate =
    previousSchoolYears.length > 0 && !currentStoreData
      ? previousSchoolYears[0].ends_on
      : currentStoreData?.starts_on;

  // Previous school years without the one that is being edited
  const filteredPreviousSchoolYears = currentStoreData
    ? previousSchoolYears.filter(
        (year) =>
          year.starts_on !== currentStoreData.starts_on &&
          year.ends_on !== currentStoreData.ends_on
      )
    : previousSchoolYears;

  const previousYearEndDate =
    filteredPreviousSchoolYears.length > 0
      ? moment(filteredPreviousSchoolYears[0].ends_on)
          .add(1, "days")
          .format("YYYY-MM-DD")
      : null;

  const yearStartsOn = moment(currentStoreData?.starts_on);
  const firstReport = useSelector((state: RootState) =>
    getReportByMoment(state, yearStartsOn)
  );

  const selectedSchoolYear = useSelector(
    getSchoolYearByDate(
      getModelDateFormat(currentStoreData) || moment().format("YYYY-MM-DD")
    )
  );

  const isLocked = useSelector((state: RootState) =>
    getIsSyncableLocked(state, currentStoreData)
  );

  // We let users edit inital stock if
  // - They did not close the report for the first month.
  // - They are updating current school year.
  const isInitialStockEditable =
    !getIsReportLocked(firstReport) &&
    (currentYear === null || currentYear === selectedSchoolYear);

  const initialCarryOverStock = useSelector((state: RootState) =>
    getInitialCarryOverStock(state, carryOverDate, firstSchoolYear)
  );

  const initialCarryOverPurchaseStock = useSelector((state: RootState) =>
    getInitialCarryOverPurchaseStock(state, carryOverDate, firstSchoolYear)
  );

  /**
   * Using Age group levels therefore we only display levels that are mapped to comet age groups.
   * This also allows us to use custom names
   */
  const ageGroupLevels = useSelector(getLevelAgeMappings);
  const levels = ageGroupLevels.length > 0 ? ageGroupLevels : allLevels;

  // All the active levels
  const activeLevels =
    ageGroupLevels.length > 0
      ? ageGroupLevels.filter((item: any) => item.active)
      : allLevels;

  // The levels that are in the current school year, but not active
  // (they were deactivated after the current school year was created)
  const currentSchoolYearLevels = levels.filter(
    (item: any) =>
      currentStoreData?.levels?.find((level) => level.level === item.level) &&
      !activeLevels.find((level: any) => level.level === item.level)
  );

  // The options to be used in the Level Select dropdown
  const levelsOptions = activeLevels
    .concat(currentSchoolYearLevels)
    .sort((a: any, b: any) => (a.level < b.level ? -1 : 1));

  const {
    updateStore,
    closeModal,
    submitBtnRef,
    submitViaBtnRefClick,
  } = useStore();

  const type =
    currentStoreData && currentStoreData.client_id ? "update" : "create";

  const allStores = useSelector(getAllStores);

  const correspondingDelivery = allStores.find((store: any) => {
    const date = store?.occurred_at?.split("T")[0];
    const startsOn = moment(currentStoreData?.starts_on).format("YYYY-MM-DD");
    return (
      isDeliveryStore(store) &&
      date === startsOn &&
      store.meta?.is_initial_stock === true &&
      store.type !== "delete"
    );
  });

  const correspondingPurchaseDetail = allStores.find((store: any) => {
    const date = store?.occurred_at?.split("T")[0];
    const startsOn = moment(currentStoreData?.starts_on).format("YYYY-MM-DD");
    return (
      isPurchaseDetailStore(store) &&
      date === startsOn &&
      store.meta?.is_initial_stock === true &&
      store.type !== "delete"
    );
  });

  const methods = useForm({
    defaultValues: currentStoreData
      ? storeToFormValues(currentStoreData)
      : { levels: [{}] },
    resolver: schoolYearValidationResolver,
    context: {
      currentStoreData,
      isLocked,
    },
  } as any) as any;

  const { control, handleSubmit, errors, register, watch } = methods;

  const { fields: levelRows, append, remove } = useFieldArray({
    control,
    name: "levels",
  });

  const morningChecked = watch("has_morning_classes");
  const afternoonChecked = watch("has_afternoon_classes");
  const startsOnWatch = watch("starts_on");

  const onSubmit = (formValues: any) => {
    const values = { ...formValues };

    const date = moment(values.starts_on).format("YYYY-MM-DD");
    const time = "13:00";
    const dateTime = moment(date + " " + time);

    // The initial stock delivery/purchase (managed in InitialStockEdit)
    // occurs on the school year's start date, so it must follow along when
    // the start date changes
    if (
      firstSchoolYear &&
      isInitialStockEditable &&
      currentStoreData?.client_id &&
      currentStoreData.starts_on !== values.starts_on
    ) {
      [correspondingDelivery, correspondingPurchaseDetail].forEach(
        (store: any) => {
          if (!store) return;
          updateStore({
            id: store.client_id,
            values: {
              ...store,
              occurred_at: moment(dateTime, "YYYY-MM-DD HH:mm").toISOString(),
            },
            type: "update",
            category: store.category,
          });
        }
      );
    }

    const shouldOpenInitialStock = type === "create" && firstSchoolYear;
    const shouldOpenCarryOver =
      type === "create" &&
      !firstSchoolYear &&
      (Object.keys(initialCarryOverStock).length > 0 ||
        Object.keys(initialCarryOverPurchaseStock).length > 0);

    updateStore(
      {
        id: (currentStoreData && currentStoreData.client_id) || undefined,
        values: formValuesToStore(values),
        model: "year",
        type: type,
        category: deliveryCategory,
      },
      shouldOpenInitialStock
        ? (storeActionData: any) =>
            `/school/${params.schoolId}/info/${storeActionData.data.id}/initial-stock`
        : shouldOpenCarryOver
        ? (storeActionData: any) =>
            `/school/${params.schoolId}/info/${storeActionData.data.id}/carry-over`
        : undefined
    );
  };

  const weekdays = moment.weekdays();
  weekdays.push(weekdays.shift()!);

  const weekdayNotChecked = () => {
    if (moment().day() === ("null" as any)) {
      return { requiredLabelClasses };
    }
  };

  const requiredLabelClasses = (
    <div
      className="wfp--form-required"
      style={{ borderColor: " 2px solid #C5192D" }}
    >
      <FormattedMessage
        id="DeliveriesEdit.commodityErrorOptions"
        defaultMessage="* Select at least one of these options"
      />
    </div>
  );

  return (
    <ModalExtended
      hideClose
      onRequestSubmit={submitViaBtnRefClick}
      onRequestClose={() => {}}
      onSecondarySubmit={() => closeModal()}
      modalLabel={intl.formatMessage({
        id: `YearEdit.edit_school_year`,
        defaultMessage: `Edit school year`,
      })}
      modalHeading={intl.formatMessage({
        id: `YearEdit.edit_school_year`,
        defaultMessage: `Edit school year`,
      })}
      primaryButtonText={intl.formatMessage({
        id: `YearEdit.save_school_year`,
        defaultMessage: `Save school year`,
      })}
      className="year-edit"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="wfp--form wfp--form-long">
          <ErrorList errors={errors} />
          <Blockquote kind="info">
            <p className={isInitialStockEditable ? "enabled" : "disabled"}>
              <FormattedMessage
                id="YearEdit.newYearMessage"
                defaultMessage="When a new school year is created, you are no longer able to edit previous years data."
              />
            </p>
            <p className={!isInitialStockEditable ? "enabled" : "disabled"}>
              <FormattedMessage
                id="YearEdit.newYearMessageUneditable"
                defaultMessage="Here you can edit the end date of the school year, if required. If you need to change any other school year information, please close this school year (by setting the end date to today or any day in the past without excluding days for which you already entered data) and create a new one."
              />
            </p>
          </Blockquote>

          <Controller
            as={<DateInput />}
            min={previousYearEndDate}
            max={null}
            name="starts_on"
            labelText={
              <FormattedMessage
                id="yearShow.start_date"
                defaultMessage="Start date"
              />
            }
            control={control}
            disabled={isLocked || !isInitialStockEditable}
          />
          <Controller
            as={<DateInput />}
            min={startsOnWatch || previousYearEndDate}
            max={null}
            name="ends_on"
            labelText={
              <FormattedMessage
                id="yearShow.end_date"
                defaultMessage="End date"
              />
            }
            control={control}
          />
          <div className={isInitialStockEditable ? "enabled" : "disabled"}>
            <div className="classes-info">
              <RadioButtonGroup
                required
                labelText={
                  <FormattedMessage
                    id="yearShow.school_days"
                    defaultMessage="School days"
                  />
                }
                helperText={
                  <FormattedMessage
                    id="YearEdit.on_which_days_is_school_open"
                    defaultMessage="On which days is the school open"
                  />
                }
              >
                {weekdays.map((weekday, i) => (
                  <Controller
                    control={control}
                    render={({ onChange, value }) => (
                      <Checkbox
                        key={weekday}
                        labelText={weekday}
                        id={`weekdays[${i}]`}
                        name={`weekdays[${i}]`}
                        disabled={isLocked}
                        checked={value || false}
                        onChange={(e: BaseSyntheticEvent) =>
                          onChange(e.target.checked)
                        }
                      />
                    )}
                    defaultValue={false}
                    name={`weekdays[${i}]`}
                  />
                ))}
              </RadioButtonGroup>
              <div>
                {errors?.weekdaysSome &&
                  weekdayNotChecked() &&
                  requiredLabelClasses}
              </div>
            </div>

            <div className="classes-info">
              <RadioButtonGroup
                labelText={intl.formatMessage({
                  id: `YearEdit.classes`,
                  defaultMessage: `Classes`,
                })}
                name="shiftsSome"
              >
                <Checkbox
                  labelText={
                    <FormattedMessage
                      id="YearEdit.morning_classes"
                      defaultMessage="Morning classes"
                    />
                  }
                  inputRef={register}
                  id="has_morning_classes"
                  name="has_morning_classes"
                  disabled={isLocked}
                />
                <Checkbox
                  labelText={
                    <FormattedMessage
                      id="YearEdit.afternoon_classes"
                      defaultMessage="Afternoon classes"
                    />
                  }
                  inputRef={register}
                  id="has_afternoon_classes"
                  name={"has_afternoon_classes"}
                  disabled={isLocked}
                />
              </RadioButtonGroup>
              <div>
                {errors?.shiftsSome &&
                  !morningChecked &&
                  !afternoonChecked &&
                  requiredLabelClasses}
              </div>
            </div>
          </div>

          {morningChecked && afternoonChecked && (
            <div className={isInitialStockEditable ? "enabled" : "disabled"}>
              <RadioButtonGroup
                labelText={
                  <FormattedMessage
                    id="YearEdit.afternoon_classes"
                    defaultMessage="Afternoon classes"
                  />
                }
              >
                <Checkbox
                  labelText={
                    <FormattedMessage
                      id="YearEdit.same_students_in_afternoon_as_morning"
                      defaultMessage="Same students in afternoon as in morning classes"
                    />
                  }
                  id="has_same_students_in_both_shifts"
                  name="has_same_students_in_both_shifts"
                  inputRef={register}
                  disabled={isLocked}
                />
              </RadioButtonGroup>
            </div>
          )}

          <div className={isInitialStockEditable ? "enabled" : "disabled"}>
            <Gate isForbidden={isLocked}>
              <Repeater>
                <RepeaterTitle>
                  <FormattedMessage
                    id="YearEdit.school_levels"
                    defaultMessage="Level Selection"
                  />
                </RepeaterTitle>

                {levelRows.map((levelRow, i) => {
                  const inactiveLevelButInSchoolYear = currentSchoolYearLevels.find(
                    (item: any) => item.level === levelRow.level
                  );
                  return (
                    <RepeaterItem
                      key={levelRow.id}
                      className="year-edit-repeater"
                      disableRemove={levelRows.length === 1}
                      removeAction={() => remove(i)}
                    >
                      <div className="repeater-wrapper">
                        {inactiveLevelButInSchoolYear && (
                          <Blockquote kind="warning">
                            <FormattedMessage
                              id="YearEdit.inactiveLevelWarning"
                              defaultMessage="This level is currently inactive."
                            />
                          </Blockquote>
                        )}
                        <div className={styles.yearRepeaterLevel}>
                          <Input
                            invalid={errors[`levels[${i}].level`] !== undefined}
                            invalidText={
                              errors[`levels[${i}].level`] &&
                              errors[`levels[${i}].level`].message
                            }
                          >
                            {() => (
                              <Select
                                labelText={
                                  <FormattedMessage
                                    id="YearEdit.levelsSelectLabelText"
                                    defaultMessage="Select"
                                  />
                                }
                                inputRef={register()}
                                className="level"
                                name={`levels[${i}].level`}
                              >
                                <SelectItem
                                  value=""
                                  text={intl.formatMessage({
                                    id: `YearEdit.select__level`,
                                    defaultMessage: `Levels`,
                                  })}
                                />
                                {levelsOptions.map(
                                  ({ value, level, custom_name }: any) => {
                                    let label = intl.formatMessage({
                                      id: `Common.${value ? value : level}`,
                                    });

                                    if (custom_name) {
                                      label = custom_name;
                                    }

                                    return (
                                      <SelectItem
                                        key={value ? value : level}
                                        value={value ? value : level}
                                        text={label}
                                      />
                                    );
                                  }
                                )}
                              </Select>
                            )}
                          </Input>
                        </div>

                        <h4 className={styles.yearRepeaterTitle}>
                          <FormattedMessage
                            id="YearEdit.initial_enrolment"
                            defaultMessage="Initial enrolment"
                          />
                        </h4>

                        <div className={styles.yearRepeaterEnrolment}>
                          {studentKinds.map((kind) => (
                            <Controller
                              key={kind.key}
                              as={<NumberInput />}
                              hideControls
                              control={control}
                              styles={
                                errors?.hasOwnProperty(kind.key)
                                  ? selectContainerStyles.invalid
                                  : selectContainerStyles.valid
                              }
                              labelText={kind.labelTrans}
                              inputRef={register()}
                              min={0}
                              name={`levels[${i}].initial_${kind.key}`}
                              invalid={
                                errors[`levels[${i}].initial_${kind.key}`]
                              }
                              invalidText={
                                errors[`levels[${i}].initial_${kind.key}`] &&
                                errors[`levels[${i}].initial_${kind.key}`]
                                  .message
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </RepeaterItem>
                  );
                })}
              </Repeater>

              <AddButton
                text={intl.formatMessage({
                  id: `YearEdit.add_school_level`,
                  defaultMessage: `Add school level`,
                })}
                onClick={() => append({})}
              />
            </Gate>
          </div>
        </div>
        <button className="hidden-btn" ref={submitBtnRef as any} />
      </form>
    </ModalExtended>
  );
};

export default YearEdit;
